import { ButtonType, ButtonWidget, TabStyles } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { BrowserTitleService } from '~commonInfra/services/BrowserTitleService';
import { Telemetry } from '~commonInfra/services/Telemetry';
import { BuyLicensesTelemetryEvent, TelemetryEvents } from '~commonInfra/telemetryEvents/TelemetryEvents';
import { AddUsersButtonStrategy } from '~src/authentication/components/identityPools/userImport/AddUsersButtonStrategy';
import { AllUserIdentitiesSubspaceStyles } from '~src/authentication/styles/AllUserIdentitiesSubspaceStyles';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { ServerSettingsUnauthenticated } from '~src/settings/services/ServerSettingsUnauthenticated';
import { BreadcrumbStateInfo } from '~src/spacesPlatform/components/breadcrumbs/BreadcrumbStateInfo';
import { ConnectedExplorer } from '~src/spacesPlatform/components/Explorer/ConnectedExplorer';
import { hasSiteLicensingConsumptionInfo } from '~src/userManagement/services/licenses/LicensesService';

import { Messages } from '../../../../codegen/Localize';
import { LicenseInfoBar } from '../../../../license/components/header/LicenseInfoBar';
import { getSessionManagementServiceInstance } from '../../../../sessionManagement/SessionManagementService';
import { ISiteAuthenticationSettings, SiteAuthenticationSettingsService } from '../../../../settings/siteAuthSettings/services/SiteAuthenticationSettingsService';
import { ExplorerStyles } from '../../../../spacesPlatform/components/Explorer/ExplorerStyles';
import { SpaceHeaderWithTotalItemsCount } from '../../../../spacesPlatform/components/header/SpaceHeaderWithTotalItemsCount';
import { SpacesSiteUrls } from '../../../../spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { SpaceContainerStyles } from '../../../../spacesPlatform/style/SpaceContainerStyles';
import { AddUsersButton } from '../../../components/AddUsersButton';
import { AdminProfilesResource } from '../../../services/resources/AdminProfilesResource';
import { ISiteUserInfo } from '../../models/ISiteUserInfo';
import { UserQuotaIncrease } from '../../serverApi/requests/ExpandUserQuotaRequest';
import { BuyModal } from './BuyLicenseDialog';
import { LeavingBuyModal, redirectToWebstore } from './LeavingBuyLicenseDialog';
import { SiteUserAuthenticationProviderContext } from './UserAuthenticationProviderContext';

export interface AllSiteUsersSpaceOwnProps {
  readonly siteName: string;
  readonly urls: SpacesSiteUrls;
  readonly shouldRenderAsSubspace: boolean;
}

export interface AllSiteUsersSpaceStateProps {
  readonly isBusyFetchingItems: boolean;
  readonly doesTheServerHaveMoreThanOneSite: boolean;
  readonly numberOfFetchedUsers: number;
  readonly isSessionServerAdmin: boolean;
  readonly canAddUsers: boolean;
}

export interface AllSiteUsersSpaceDispatchProps {
  readonly requestSpaceContents: () => void;
  readonly updateBreadcrumbs: (breadcrumbs: ReadonlyArray<BreadcrumbStateInfo>) => void;
  readonly fetchResourceActions: () => void;
}

export type AllSiteUsersSpaceProps = AllSiteUsersSpaceOwnProps & AllSiteUsersSpaceStateProps & AllSiteUsersSpaceDispatchProps;

type BuyLicensesDisplayType = 'DEFAULT' | 'WEBSTORE-MODAL' | 'ADDMENU';

export interface AllSiteUsersSpaceState {
  readonly displayLicensingBar: boolean;
  readonly hasSiteLicensingInfo: boolean;
  readonly renderChildren: boolean;
  readonly siteAuthenticationSettings: ISiteAuthenticationSettings;
  readonly buyLicensesEnabled: boolean;
  readonly buyLicensesDisplayType: BuyLicensesDisplayType;
  readonly displayBuyModal: boolean;
  readonly displayLeavingBuyModal: boolean;
}

export class AllSiteUsersSpace extends React.PureComponent<AllSiteUsersSpaceProps, AllSiteUsersSpaceState> {
  public constructor(props: AllSiteUsersSpaceProps) {
    super(props);
    this.state = { displayLicensingBar: undefined, hasSiteLicensingInfo: false, renderChildren: false, siteAuthenticationSettings: undefined, buyLicensesEnabled: false, buyLicensesDisplayType: undefined, displayBuyModal: false, displayLeavingBuyModal: false };
  }

  public componentDidMount() {
    this.props.updateBreadcrumbs([getSiteUsersSpaceBreadcrumb(this.props.urls, this.props.doesTheServerHaveMoreThanOneSite)]);
    BrowserTitleService.setTitleWithPlaceEntityName({ placeEntityName: this.props.doesTheServerHaveMoreThanOneSite ? Messages.siteUsers() : Messages.users() });
    this.props.requestSpaceContents();
    this.updateSiteLicenseConsumptionInfo();
    this.getSiteAuthenticationSettings();
    AdminProfilesResource.instance.prefetch();
    // Fetch resource-level actions (e.g., SEE_SITE_USERS, MODIFY_USERS) when admin profiles is enabled
    if (getSessionManagementServiceInstance().isAdminProfilesEnabled()) {
      this.props.fetchResourceActions();
    }
  }

  public componentDidUpdate(prevProps: AllSiteUsersSpaceProps) {
    if (!_.isUndefined(this.props.siteName) && prevProps.siteName !== this.props.siteName) {
      // Site switch changes admin profile scope; invalidate cache and prefetch for new site.
      AdminProfilesResource.instance.reset();
      AdminProfilesResource.instance.prefetch();
      this.props.requestSpaceContents();
    }

    if (!this.state.renderChildren && !_.isUndefined(this.state.displayLicensingBar) && !_.isUndefined(this.state.siteAuthenticationSettings)) {
      this.setState({ renderChildren: true });
    }
  }

  public render(): JSX.Element {
    if (!this.state.renderChildren) {
      return null;
    }
    return (
      <div className={SpaceContainerStyles.SpaceContainer}>
        {this.renderComponentHeader()}
        <div className={SpaceContainerStyles.SubplaceArea}>
          <SiteUserAuthenticationProviderContext.Provider value={this.authenticationProvider}>
            <ConnectedExplorer
              supportedDisplayModes={['list']}
              toolbarProps={{
                supportFilterPanel: true,
                toolbarVisibility: 'enabled',
                toolbarContentSpecificControls: { beginning: this.addUserButtonOrMenu() }
              }}
              emptyStateProps={{
                primaryMessage: Messages.usersSiteEmptyMessage()
              }}
            />
          </SiteUserAuthenticationProviderContext.Provider>
        </div>

        {this.state.displayBuyModal &&
          <BuyModal onClose={this.closeBuyModal} server={getSessionManagementServiceInstance()} reportTelemetry={this.submissionTelemetryEvent}></BuyModal>}

        {this.state.displayLeavingBuyModal &&
          <LeavingBuyModal onClose={this.closeLeavingBuyModal} server={getSessionManagementServiceInstance()}></LeavingBuyModal>}
      </div>
    );
  }

  private renderComponentHeader = (): JSX.Element => {
    if (this.props.shouldRenderAsSubspace) {
      // The space header is already defined.
      return <div className={AllUserIdentitiesSubspaceStyles.MastheadArea}>
        {this.licenseBar()}
      </div>;
    } else {
      return <SpaceHeaderWithTotalItemsCount
        onClick={this.onSpaceHeaderClick}
        label={this.props.doesTheServerHaveMoreThanOneSite ? Messages.siteUsers() : Messages.users()}
        testId={'site-users'}
        count={this.props.numberOfFetchedUsers}
        countUpToDate={!this.props.isBusyFetchingItems}
        licenseInfoBar={this.licenseBar}
      />;
    }
  };

  private authenticationProvider = (user: ISiteUserInfo) => {
    const provider = FeatureFlags.featureFlagForMultipleIDPSupportEnabled? SiteAuthenticationSettingsService.findProviderForMultipleIdp(this.state.siteAuthenticationSettings, user.authenticationProviderId, user.idpConfigurationId)
    : SiteAuthenticationSettingsService.findProvider(this.state.siteAuthenticationSettings, user.authenticationProviderId);
    if (provider) {
      return {
        name: FeatureFlags.featureFlagForMultipleIDPSupportEnabled && provider.idpConfigurationName != null ?
        provider.idpConfigurationName : SiteAuthenticationSettingsService.getProviderDisplayName(provider),
        isEnabled: provider.isEnabled
      };
    } else {
      return {
        name: Messages.unspecified(),
        isEnabled: false
      };
    }
  };

  private getSiteAuthenticationSettings = async () => {
    const siteAuthenticationSettings = await SiteAuthenticationSettingsService.getSiteAuthenticationSettings();
    this.setState({ siteAuthenticationSettings });
  };

  private updateSiteLicenseConsumptionInfo = async () => {
    const hasSiteLicensingInfo = await hasSiteLicensingConsumptionInfo();
    const showLicenseInfoBarForSingleSiteServer = this.props.isSessionServerAdmin && !this.props.doesTheServerHaveMoreThanOneSite;
    const displayLicensingBar = hasSiteLicensingInfo || showLicenseInfoBarForSingleSiteServer;
    const siteTypeName = getSessionManagementServiceInstance().siteTypeName();
    const buyLicensesEnabled = getSessionManagementServiceInstance().isBuyLicensesEnabled()
      && (siteTypeName === 'Purchase' || siteTypeName === 'Undefined');
    const buyLicensesDisplayType: BuyLicensesDisplayType = getSessionManagementServiceInstance().getBuyLicensesDisplayType() as BuyLicensesDisplayType;

    this.setState({
      hasSiteLicensingInfo,
      displayLicensingBar,
      buyLicensesEnabled,
      buyLicensesDisplayType,
    });
  };

  private licenseBar = (): JSX.Element => {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        {this.state.displayLicensingBar && (
          <LicenseInfoBar
            hasSiteLicensingInfo={this.state.hasSiteLicensingInfo}
          />
        )}
        {this.state.buyLicensesEnabled && (this.state.buyLicensesDisplayType === 'DEFAULT' || this.state.buyLicensesDisplayType === 'WEBSTORE-MODAL') && (
          <>
            <span className={ExplorerStyles.ToolbarButtonDivider} style={{ marginTop: TabStyles.Spacing.SmallSpace, marginLeft: TabStyles.Spacing.LargeSpace, marginRight: TabStyles.Spacing.MediumSpace }} />
            <div style={{ paddingTop: '2px' }}>
              <BuyLicensesButton
                handleClick={this.buyLicense}
                text={this.state.buyLicensesDisplayType === 'WEBSTORE-MODAL' ? Messages.buyLicensesBuyMoreLicenses() : Messages.buyLicenses()}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  private addUserButtonOrMenu = () => {
    if (!this.props.canAddUsers) {
      return null;
    }
    if (!this.state.buyLicensesEnabled) {
      return this.addUserButton(false);
    }
    var siteUuid = getSessionManagementServiceInstance().siteUuid();
    if (this.state.buyLicensesDisplayType === 'ADDMENU') {
      Telemetry.sendEvent<BuyLicensesTelemetryEvent>({
        type: TelemetryEvents.BuyLicenses,
        action: 'buy-licenses-button-2-displayed',
        siteUuid: siteUuid
      });
      return this.addUserButton(true);
    }
    else if (this.state.buyLicensesDisplayType === 'WEBSTORE-MODAL') {
      return this.addUserButton(false);
    }
    else {
      Telemetry.sendEvent<BuyLicensesTelemetryEvent>({
        type: TelemetryEvents.BuyLicenses,
        action: 'buy-licenses-button-1-displayed',
        siteUuid: siteUuid
      });
      return this.addUserButton(false);
    }
  };

  private addUserButton = (showBuyLicensesOption: boolean) => {
    const canImportUsersFromFile = getSessionManagementServiceInstance().canSessionManageUsersOrActOnResource('User', ['IMPORT_USERS']);
    if (FeatureFlags.identityPoolsEnabled) {
      return <AddUsersButtonStrategy
                isServerUsersSpace={false}
                canImportUsersFromFile={canImportUsersFromFile}
                requestSpaceContents={this.props.requestSpaceContents}
                showBuyLicensesOption={showBuyLicensesOption}
                buyLicense={this.buyLicense}
                addButtonText={Messages.addUsers()} />;
    } else {
      return (
        <AddUsersButton isServerUsersSpace={false}
          canImportUsersFromFile={canImportUsersFromFile}
          requestSpaceContents={this.props.requestSpaceContents}
          showBuyLicensesOption={showBuyLicensesOption}
          buyLicense={this.buyLicense}
          addButtonText={Messages.addUsers()}
          identityStoreType={ServerSettingsUnauthenticated.domainFamily()}
        />
      );
    }
  };

  private buyLicense = () => {
    var siteUuid = getSessionManagementServiceInstance().siteUuid();
    const action = this.getTelemetryActionForBuyButtonClick();

    // for WEBSTORE-MODAL display with confirmation dialog disabled, do not send telemetry event here.
    // it will be sent when redirectToWebstore() is invoked
    if (this.state.buyLicensesDisplayType !== 'WEBSTORE-MODAL' || FeatureFlags.redirectWebstoreConfirmationDialogEnabled) {
      Telemetry.sendEvent<BuyLicensesTelemetryEvent>({
        type: TelemetryEvents.BuyLicenses,
        action: action,
        siteUuid: siteUuid
      });
    }

    if (this.state.buyLicensesDisplayType === 'WEBSTORE-MODAL') {
      if (FeatureFlags.redirectWebstoreConfirmationDialogEnabled) {
        this.openLeavingBuyModal();
      } else {
        const baseUrl = getSessionManagementServiceInstance().webstoreUrl();
        redirectToWebstore(baseUrl, siteUuid, getSessionManagementServiceInstance().org62RecordId());
      }
    } else {
      this.openBuyModal();
    }
  };

  private getTelemetryActionForBuyButtonClick = (): BuyLicensesTelemetryEvent['action'] => {

    switch (this.state.buyLicensesDisplayType) {
      case 'ADDMENU':
        return 'buy-licenses-button-2-clicked';
      case 'WEBSTORE-MODAL':
        return 'buy-licenses-button-3-clicked';
      default:
        return 'buy-licenses-button-1-clicked';
    }
  };

  private submissionTelemetryEvent = (orderQuantities: UserQuotaIncrease): void => {
    let action: BuyLicensesTelemetryEvent['action'] = 'buy-licenses-button-1-submit';
    if (this.state.buyLicensesDisplayType === 'ADDMENU') {
      action = 'buy-licenses-button-2-submit';
    }
    Telemetry.sendEvent<BuyLicensesTelemetryEvent>({
      type: TelemetryEvents.BuyLicenses,
      action: action,
      ...orderQuantities
    });
  };

  private openBuyModal = (): void => {
    this.setState({ displayBuyModal: true, });
  };

  private closeBuyModal = (): void => {
    this.setState({ displayBuyModal: false, });
  };

  private openLeavingBuyModal = (): void => {
    this.setState({ displayLeavingBuyModal: true, });
  };

  private closeLeavingBuyModal = (): void => {
    this.setState({ displayLeavingBuyModal: false, });
  };

  private onSpaceHeaderClick = () => {
    this.props.requestSpaceContents();
  };

}

interface BuyLicensesButtonProps {
  readonly handleClick: () => void;
  readonly text: string;
}
const BuyLicensesButton: React.FC<BuyLicensesButtonProps> = (props: BuyLicensesButtonProps) => {

  return (
    <ButtonWidget
      children={props.text}
      buttonType={ButtonType.LowEmphasis}
      handleClick={props.handleClick}
      tb-test-id={'buy-licenses'}
      testId={'buy-licenses'} />
  );
};

export function getSiteUsersSpaceBreadcrumb(urls: SpacesSiteUrls, multiSite: boolean): BreadcrumbStateInfo {
  return {
    id: urls && urls.usersUrl(),
    text: multiSite ? Messages.allSiteUsers() : Messages.allUsers(),
    href: urls && urls.usersUrl(),
    testId: 'site-users-link'
  };
}
